import { useState } from 'react'
import { cn } from '@/lib/utils'
import type {
  Assay, AssayCategoryRow, AssayItem, AssayLatencyBucket, AssaySourceRow, AssaySubjectRow,
} from '../api'
import { CREDIBILITY_MEANING, RELIABILITY_MEANING, fmtLatency, pct, share } from './scale'

/**
 * The console's rails and readouts.
 *
 * ## Everything here states a denominator
 *
 * A dossier screen that prints "412 items" has told the reader nothing they can act on. Every
 * count in this file is rendered against what it is a count OF — items against the window,
 * quotable against total, sources against the file they are the sole source for — because the
 * whole subject of this board is proportion, and a bare number invites the reader to supply
 * their own denominator, which is where confidence in a record actually goes wrong.
 *
 * ## Nothing in here is a chart for its own sake
 *
 * Each rail row is a control: it narrows the plate and the chronology. A list beside a figure
 * that cannot drive the figure is a second thing to read, and a reader with two accounts of
 * the same corpus and no link between them ends up trusting neither.
 */

// ── structure ────────────────────────────────────────────────────────────────

/**
 * A ruled section of a rail.
 *
 * ## Why the cap is on the LIST and not on the rail
 *
 * These rails hold unbounded tables: a register can run to hundreds of files and every
 * publication that has ever been graded. Left to grow, one long section pushes the other two
 * off the bottom, and the only way to reach Coverage — the section that reports the GAPS, which
 * is the one thing here nothing else on the screen says — is to scroll past four hundred files.
 * Capping each body at a fixed height and letting it scroll inside itself keeps all three
 * reachable at once, which is the property that matters when somebody is looking for one
 * specific thing.
 *
 * The header stays outside the scrolling area on purpose, so the count and the collapse control
 * never scroll away from the table they describe.
 *
 * ## Collapsed state is remembered, per browser
 *
 * An analyst who closes Publications because they only care about one outlet should not have to
 * close it again on the next visit. Stored locally rather than on the account: this is a
 * preference about a screen, not a fact about the record, and syncing it would be a promise
 * that then has to be kept.
 *
 * ## The right rail deliberately passes neither
 *
 * A detail panel is not a list, and capping one hides its actions behind a scrollbar nobody
 * looks for. Omitting `maxBodyHeight` leaves the section growing freely, which is what the
 * selected-item and standing panels want.
 */
export function Section({ figure, title, note, children, storageKey, maxBodyHeight = 0 }: {
  /** "Figure 3", "Table 1" — the console is laid out as a plate, and plates number things. */
  figure?: string
  title: string
  note?: React.ReactNode
  children: React.ReactNode
  /** Stable key for remembering the collapsed state. Omit to make the section always open. */
  storageKey?: string
  /** Cap for the scrolling body, in px. 0 (the default) lets the section grow freely. */
  maxBodyHeight?: number
}) {
  const [open, setOpen] = useState(() => {
    if (!storageKey) return true
    try {
      return window.localStorage.getItem(`axis.assay.rail.${storageKey}`) !== 'closed'
    } catch {
      // Private windows and blocked site data throw on access. A section that cannot
      // remember its state should still render — open, which is the useful default.
      return true
    }
  })

  const toggle = () => setOpen((was) => {
    const next = !was
    if (storageKey) {
      try {
        window.localStorage.setItem(`axis.assay.rail.${storageKey}`, next ? 'open' : 'closed')
      } catch { /* a preference is never worth failing the interaction over */ }
    }
    return next
  })

  const bodyId = `assay-rail-${storageKey ?? title.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <section className="border-b px-4 py-3.5" style={{ borderColor: 'var(--plate-rule)' }}>
      <header className="mb-2.5 flex items-baseline justify-between gap-2">
        {storageKey ? (
          <button type="button" onClick={toggle} aria-expanded={open} aria-controls={bodyId}
                  className="flex items-center gap-1.5 text-left">
            <span aria-hidden
                  className={cn('inline-block font-mono text-[8px] leading-none',
                    'transition-transform', open ? 'rotate-90' : 'rotate-0')}
                  style={{ color: 'var(--plate-ink-faint)' }}>▶</span>
            <h2 className="axis-plate-eyebrow">
              {figure ? <span style={{ opacity: 0.6 }}>{figure} · </span> : null}{title}
            </h2>
          </button>
        ) : (
          <h2 className="axis-plate-eyebrow">
            {figure ? <span style={{ opacity: 0.6 }}>{figure} · </span> : null}{title}
          </h2>
        )}
        {note ? <span className="axis-plate-mini tabular-nums">{note}</span> : null}
      </header>
      <div
        id={bodyId}
        hidden={!open}
        className={cn(maxBodyHeight > 0 && 'axis-plate-scroll overflow-y-auto')}
        style={maxBodyHeight > 0 ? { maxHeight: maxBodyHeight } : undefined}
      >
        {children}
      </div>
    </section>
  )
}

/** A share, drawn as a rule with a filled portion. Ink, never a colour ramp. */
export function Bar({ value, tone = 'above', height = 4 }: {
  value: number
  tone?: 'above' | 'below' | 'ink' | 'dispute'
  height?: number
}) {
  const token = tone === 'above' ? 'var(--plate-above)'
    : tone === 'below' ? 'var(--plate-below)'
    : tone === 'dispute' ? 'var(--plate-dispute)'
    : 'var(--plate-ink-dim)'
  return (
    <span className="block w-full overflow-hidden"
          style={{ height, background: 'var(--plate-rule)' }}>
      <span className="block h-full" style={{ width: pct(value), background: token }} />
    </span>
  )
}

export function Stat({ label, value, note, tone }: {
  label: string
  value: React.ReactNode
  note?: string
  tone?: 'above' | 'below' | 'dispute'
}) {
  const ink = tone === 'below' ? 'var(--plate-below)'
    : tone === 'dispute' ? 'var(--plate-dispute)'
    : tone === 'above' ? 'var(--plate-above)'
    : 'var(--plate-ink)'
  return (
    <div className="flex flex-col items-end gap-0.5" title={note}>
      <i className="axis-plate-eyebrow not-italic">{label}</i>
      <b className="font-mono text-[15px] font-normal tabular-nums" style={{ color: ink }}>
        {value}
      </b>
    </div>
  )
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-2 text-[11px] leading-relaxed"
            style={{ color: 'var(--plate-ink-faint)' }}>{children}</p>
}

// ── figure 2: how late we were ───────────────────────────────────────────────

/**
 * The custody-delay profile.
 *
 * ## Why "No event date" is a bar and not a footnote
 *
 * It is the honest sixth bucket. An item whose event date is unknown has no measurable delay,
 * and the two available alternatives were both worse: dropping it makes the profile describe a
 * subset while looking like it describes the archive, and counting it as instant makes the
 * archive look fastest exactly where it knows least. Drawn in outline rather than fill,
 * because it is a bucket of unmeasured things, not a fast one.
 */
export function LatencyProfile({ buckets, p50, p90 }: {
  buckets: AssayLatencyBucket[]
  p50: number
  p90: number
}) {
  const peak = Math.max(1, ...buckets.map((b) => b.items))
  const total = buckets.reduce((n, b) => n + b.items, 0)
  return (
    // Capped: with the rails hidden this figure gets the full width, and a bar a thousand
    // pixels long stops being comparable to the one under it — the eye reads length, and
    // length it cannot take in at a glance is not a reading.
    <figure className="m-0 flex min-w-0 max-w-[560px] flex-col gap-2">
      <figcaption className="flex items-baseline justify-between gap-3">
        <span className="axis-plate-eyebrow">How long it took to reach us</span>
        <span className="axis-plate-mini">Event date → the day we took custody</span>
      </figcaption>

      <div className="flex flex-col gap-[3px]">
        {buckets.map((bucket) => {
          const undated = bucket.code === 'UNDATED'
          return (
            <div key={bucket.code} className="grid items-center gap-2"
                 style={{ gridTemplateColumns: '92px 1fr 46px' }}>
              <span className="truncate text-[10.5px]"
                    style={{ color: undated ? 'var(--plate-ink-faint)' : 'var(--plate-ink-dim)' }}>
                {bucket.label}
              </span>
              <span className="block h-[9px]" style={{ background: 'var(--plate-paper-edge)' }}>
                <span className="block h-full"
                      style={{
                        width: `${(bucket.items / peak) * 100}%`,
                        background: undated ? 'transparent' : 'var(--plate-ink-dim)',
                        opacity: undated ? 1 : 0.72,
                        border: undated ? '1px dashed var(--plate-ink-faint)' : undefined,
                      }} />
              </span>
              <span className="text-right font-mono text-[10px] tabular-nums"
                    style={{ color: 'var(--plate-ink-faint)' }}>
                {total > 0 ? pct(share(bucket.items, total)) : '—'}
              </span>
            </div>
          )
        })}
      </div>

      <p className="axis-plate-mini leading-relaxed">
        Half of it reached the file within <b className="font-normal"
          style={{ color: 'var(--plate-ink-dim)' }}>{fmtLatency(p50)}</b>; the slowest one in
        ten took <b className="font-normal"
          style={{ color: 'var(--plate-ink-dim)' }}>{fmtLatency(p90)}</b> or more. Counted over
        dated items only — an item with no event date has no delay to measure.
      </p>
    </figure>
  )
}

// ── the left rail ────────────────────────────────────────────────────────────

/** Files, ranked by what they hold, with the share that could actually be quoted. */
export function FileList({ subjects, selectedId, onPick }: {
  subjects: AssaySubjectRow[]
  selectedId: string | null
  onPick: (id: string | null) => void
}) {
  if (!subjects.length) return <Empty>No file has an item in this window.</Empty>
  return (
    <div className="flex flex-col">
      {subjects.map((s) => {
        const quotable = share(s.atOrAboveFloor, s.items)
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onPick(selectedId === s.id ? null : s.id)}
            title={`${s.items} items · ${s.atOrAboveFloor} at or above the floor · `
              + `${s.sources} source${s.sources === 1 ? '' : 's'}`
              + (s.sources === 1 ? ' — the whole file rests on one publication' : '')}
            className={cn('axis-plate-row', selectedId === s.id && 'axis-plate-row-on')}
          >
            <span className="flex items-baseline gap-2">
              <span className="flex-1 truncate text-[11.5px]"
                    style={{ color: 'var(--plate-ink)' }}>{s.displayName}</span>
              {s.sources === 1 ? (
                <em className="axis-plate-mini not-italic"
                    style={{ color: 'var(--plate-below)' }}>1 SOURCE</em>
              ) : null}
              <em className="font-mono text-[10px] not-italic tabular-nums"
                  style={{ color: 'var(--plate-ink-faint)' }}>{s.items}</em>
            </span>
            <span className="mt-1 block"><Bar value={quotable} /></span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * Publications, with the reliability letter that grades everything they bring.
 *
 * A source letter is not an Admiralty PAIR and is never rendered as one: credibility is a
 * property of a claim, and printing "C3" for a source would put a digit on screen that nobody
 * assigned.
 */
export function SourceList({ sources, selectedId, onPick }: {
  sources: AssaySourceRow[]
  selectedId: string | null
  onPick: (id: string | null) => void
}) {
  if (!sources.length) return <Empty>Nothing has been collected in this window.</Empty>
  const peak = Math.max(1, ...sources.map((s) => s.items))
  return (
    <div className="flex flex-col">
      {sources.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onPick(selectedId === s.id ? null : s.id)}
          title={`${s.name} — graded ${s.reliability}: ${RELIABILITY_MEANING[s.reliability]}`
            + `\n${s.items} items, ${s.atOrAboveFloor} at or above the floor`}
          className={cn('axis-plate-row', selectedId === s.id && 'axis-plate-row-on')}
        >
          <span className="flex items-center gap-2">
            <em className="axis-plate-grade not-italic">{s.reliability}</em>
            <span className="flex-1 truncate text-[11.5px]"
                  style={{ color: 'var(--plate-ink)' }}>{s.name}</span>
            {s.contested > 0 ? (
              <em className="font-mono text-[9.5px] not-italic tabular-nums"
                  style={{ color: 'var(--plate-dispute)' }}>{s.contested}✕</em>
            ) : null}
            <em className="font-mono text-[10px] not-italic tabular-nums"
                style={{ color: 'var(--plate-ink-faint)' }}>{s.items}</em>
          </span>
          <span className="mt-1 block"><Bar value={s.items / peak} tone="ink" height={3} /></span>
        </button>
      ))}
    </div>
  )
}

/**
 * Coverage per dossier tab — including the tabs holding nothing.
 *
 * ## An empty band is the most useful row here
 *
 * It is a gap: a category the record has nothing in. Rendered as a ruled void rather than
 * omitted, because a list of what we have cannot answer the question this screen is for, which
 * is what we do not.
 */
export function CategoryCoverage({ categories, selectedCode, onPick }: {
  categories: AssayCategoryRow[]
  selectedCode: string | null
  onPick: (code: string | null) => void
}) {
  const peak = Math.max(1, ...categories.map((c) => c.items))
  return (
    <div className="flex flex-col">
      {categories.map((c) => {
        const empty = c.items === 0
        return (
          <button
            key={c.code}
            type="button"
            onClick={() => onPick(selectedCode === c.code ? null : c.code)}
            disabled={empty}
            title={empty
              ? `${c.label} — nothing filed in this window. This is a gap, not a nil return.`
              : `${c.label} — ${c.items} items, ${c.atOrAboveFloor} quotable`}
            className={cn('axis-plate-row',
              selectedCode === c.code && 'axis-plate-row-on',
              empty && 'cursor-default')}
          >
            <span className="flex items-baseline gap-2">
              <span className="flex-1 truncate text-[11.5px]"
                    style={{ color: empty ? 'var(--plate-ink-faint)' : 'var(--plate-ink)' }}>
                {c.label}
              </span>
              {empty ? (
                <em className="axis-plate-mini not-italic"
                    style={{ color: 'var(--plate-below)' }}>GAP</em>
              ) : (
                <em className="font-mono text-[10px] not-italic tabular-nums"
                    style={{ color: 'var(--plate-ink-faint)' }}>{c.items}</em>
              )}
            </span>
            <span className="mt-1 block">
              {empty
                ? <span className="axis-plate-void" />
                : <Bar value={c.items / peak} tone="ink" height={3} />}
            </span>
          </button>
        )
      })}
    </div>
  )
}

// ── the right rail ───────────────────────────────────────────────────────────

/**
 * The verdict — what this record would survive being asked about.
 *
 * Written as sentences, not as a grid of metrics. The numbers are all elsewhere on the screen;
 * what a rail can add is the reading of them, and a reading is prose. Each line names its own
 * evidence so the reader can go and check it rather than take it.
 */
export function StandingPanel({ assay }: { assay: Assay }) {
  const s = assay.standing
  const quotable = share(s.atOrAboveFloor, s.items)
  const contested = s.disputed + s.debunked
  const floor = `${assay.floor.reliability}${assay.floor.credibility}`

  return (
    <>
      <Section figure="Reading" title="What the record rests on">
        <p className="mb-3 font-serif text-[15px] leading-snug"
           style={{ color: 'var(--plate-ink)' }}>
          {s.items === 0
            ? 'Nothing has been collected in this window.'
            : <>
                <b className="font-normal" style={{ color: 'var(--plate-above)' }}>
                  {pct(quotable)}
                </b>{' '}of this window could be quoted at {floor} or better.
              </>}
        </p>
        {s.items > 0 ? (
          <>
            <Bar value={quotable} height={7} />
            <p className="mt-1.5 axis-plate-mini leading-relaxed">
              {s.atOrAboveFloor.toLocaleString()} at or above the floor ·{' '}
              {s.belowFloor.toLocaleString()} on file and not quotable
            </p>
          </>
        ) : null}
      </Section>

      {s.items > 0 ? (
        <Section title="Where it is weak">
          <ul className="flex list-none flex-col gap-2.5 p-0 text-[11.5px] leading-relaxed"
              style={{ color: 'var(--plate-ink-dim)' }}>
            <Finding
              lead={`${s.singleSourceSubjects} of ${s.subjects}`}
              tone={s.singleSourceSubjects > 0 ? 'below' : undefined}
            >
              file{s.subjects === 1 ? '' : 's'} rest entirely on a single publication. Nothing in
              them can be corroborated without going somewhere else first.
            </Finding>
            <Finding lead={fmtLatency(s.latencyP90Minutes)}>
              is how long the slowest one item in ten took to reach the file after the thing
              it describes actually happened.
            </Finding>
            <Finding
              lead={`${s.undated.toLocaleString()} item${s.undated === 1 ? '' : 's'}`}
              tone={share(s.undated, s.items) > 0.25 ? 'below' : undefined}
            >
              carry no event date at all, so their delay is unmeasured rather than short.
            </Finding>
            {contested > 0 ? (
              <Finding lead={`${contested} item${contested === 1 ? '' : 's'}`} tone="dispute">
                stand disputed or debunked. A correction here is a new item pointing at the old
                one, so both remain on the record.
              </Finding>
            ) : null}
            {s.redacted > 0 ? (
              <Finding lead={`${s.redacted} redacted`}>
                — the rows are kept and the content is gone, so the gap stays visible.
              </Finding>
            ) : null}
            {s.withheldSensitive > 0 ? (
              <Finding lead={`${s.withheldSensitive} withheld`} tone="below">
                sensitive item{s.withheldSensitive === 1 ? '' : 's'} sit outside every number on
                this board. Your authorities exclude them; somebody else's assay of the same
                window will differ.
              </Finding>
            ) : null}
          </ul>
        </Section>
      ) : null}

      <Section title="The truth ladder" note={`${s.items.toLocaleString()}`}>
        <div className="flex flex-col gap-[3px]">
          {([
            ['Verified or corroborated', s.corroborated, 'above'],
            ['Single source', s.singleSource, 'ink'],
            ['Unverified', s.unverified, 'ink'],
            ['Disputed', s.disputed, 'dispute'],
            ['Debunked', s.debunked, 'dispute'],
          ] as [string, number, 'above' | 'ink' | 'dispute'][]).map(([label, n, tone]) => (
            <div key={label} className="grid items-center gap-2"
                 style={{ gridTemplateColumns: '1fr 70px 34px' }}>
              <span className="truncate text-[11px]"
                    style={{ color: 'var(--plate-ink-dim)' }}>{label}</span>
              <Bar value={share(n, s.items)} tone={tone} height={5} />
              <span className="text-right font-mono text-[10px] tabular-nums"
                    style={{ color: 'var(--plate-ink-faint)' }}>{n}</span>
            </div>
          ))}
        </div>
        <p className="mt-2 axis-plate-mini leading-relaxed">
          Only <code style={{ color: 'var(--plate-ink-dim)' }}>intel:triage</code> moves an item
          up this ladder, and every move is audited.
        </p>
      </Section>
    </>
  )
}

function Finding({ lead, tone, children }: {
  lead: string
  tone?: 'below' | 'dispute'
  children: React.ReactNode
}) {
  const ink = tone === 'below' ? 'var(--plate-below)'
    : tone === 'dispute' ? 'var(--plate-dispute)'
    : 'var(--plate-ink)'
  return (
    <li className="border-l pl-2.5" style={{ borderColor: 'var(--plate-rule)' }}>
      <b className="font-mono text-[11.5px] font-normal tabular-nums" style={{ color: ink }}>
        {lead}
      </b>{' '}{children}
    </li>
  )
}

/**
 * One item, read in full.
 *
 * The two dates are stated separately and the interval between them is spelled out as a
 * sentence, because that interval is the reading this whole console exists to make available
 * and a reader should not have to subtract two timestamps to get it.
 */
export function ItemPanel({ item, floor, onOpenFile, onClear }: {
  item: AssayItem
  floor: { reliability: string, credibility: string }
  onOpenFile: (subjectId: string) => void
  onClear: () => void
}) {
  const grade = `${item.reliability}${item.credibility}`
  const above = item.standing >= 0
  const contested = item.veracityCode === 'DISPUTED' || item.veracityCode === 'DEBUNKED'
  return (
    <Section
      figure="Selected"
      title="Item"
      note={<button type="button" onClick={onClear}
                    className="underline underline-offset-2">Clear</button>}
    >
      <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
        <span className={cn('axis-plate-grade', !above && 'axis-plate-grade-below')}>
          {grade}
        </span>
        <span className="axis-plate-tag">{item.itemTypeCode.replaceAll('_', ' ')}</span>
        {contested ? (
          <span className="axis-plate-tag" style={{ color: 'var(--plate-dispute)' }}>
            {item.veracityCode}
          </span>
        ) : null}
        {item.redacted ? <span className="axis-plate-tag">REDACTED</span> : null}
      </div>

      <h3 className="mb-1.5 font-serif text-[15px] leading-snug font-normal"
          style={{ color: 'var(--plate-ink)' }}>
        {item.title ?? 'Untitled observation'}
      </h3>
      <p className="mb-3 axis-plate-mini">
        {item.sourceName} · {item.subjectName}
      </p>

      <p className="mb-3 border-l pl-2.5 text-[11.5px] leading-relaxed"
         style={{
           color: 'var(--plate-ink-dim)',
           borderColor: above ? 'var(--plate-above)' : 'var(--plate-below)',
         }}>
        {above
          ? `${grade} is inside the ${floor.reliability}${floor.credibility} floor, so this may `
            + 'be quoted. '
          : `${grade} is below the ${floor.reliability}${floor.credibility} floor on `
            + `${bindingAxis(item, floor)}, so it stays on the record and out of the brief. `}
        The source is {RELIABILITY_MEANING[item.reliability]} and the claim is{' '}
        {CREDIBILITY_MEANING[item.credibility]}.
      </p>

      <dl className="mb-3 grid grid-cols-[86px_1fr] gap-x-3 gap-y-1.5 text-[11px]">
        <Field label="Occurred">
          {item.occurredAt
            ? new Date(item.occurredAt).toLocaleString()
            : <span style={{ color: 'var(--plate-ink-faint)' }}>Not known</span>}
        </Field>
        <Field label="Custody">{new Date(item.collectedAt).toLocaleString()}</Field>
        <Field label="Delay">
          <b className="font-mono font-normal tabular-nums"
             style={{ color: item.latencyMinutes === null
               ? 'var(--plate-ink-faint)' : 'var(--plate-ink)' }}>
            {fmtLatency(item.latencyMinutes)}
          </b>
        </Field>
        <Field label="Category">{item.categoryCode.replaceAll('_', ' ').toLowerCase()}</Field>
      </dl>

      <div className="flex flex-col gap-1.5">
        <button type="button" onClick={() => onOpenFile(item.subjectId)}
                className="axis-plate-button">
          Open the file →
        </button>
        {item.url ? (
          <a href={item.url} target="_blank" rel="noreferrer noopener"
             className="axis-plate-button text-center">
            The original ↗
          </a>
        ) : null}
      </div>
    </Section>
  )
}

/** Which half of the pair is actually keeping this item out. Never both, never an average. */
function bindingAxis(
  item: AssayItem, floor: { reliability: string, credibility: string },
): string {
  const rel = floor.reliability.charCodeAt(0) - item.reliability.charCodeAt(0)
  const cred = floor.credibility.charCodeAt(0) - item.credibility.charCodeAt(0)
  if (rel < cred) return `source reliability (${item.reliability})`
  if (cred < rel) return `information credibility (${item.credibility})`
  return 'both axes'
}

function Field({ label, children }: { label: string, children: React.ReactNode }) {
  return (
    <>
      <dt className="axis-plate-eyebrow">{label}</dt>
      <dd className="m-0" style={{ color: 'var(--plate-ink-dim)' }}>{children}</dd>
    </>
  )
}
